// View State Management
export type ViewState =
  | 'landing'
  | 'about'
  | 'why_demo'
  | 'guide'
  | 'legal'
  | 'legal_about'
  | 'legal_privacy'
  | 'legal_transactions'
  | 'legal_contact'
  | 'pricing'
  | 'posters'
  | 'lp_construction'
  | 'lp_manufacturing'
  | 'lp_logistics'
  | 'qr_invalid'
  | 'worker_landing'
  | 'worker_form'
  | 'worker_confirm'
  | 'worker_success'
  | 'login'
  | 'manager_dashboard'
  | 'manager_cases'
  | 'manager_case_detail'
  | 'manager_import'
  | 'manager_qr'
  | 'manager_settings'
  | 'manager_report'
  | 'operator_usage'
  | 'my_report'
  | 'my_reports_list';

// Legal Tab Type
export type LegalTab = 'about' | 'privacy' | 'transactions' | 'contact';

// Risk Level Enumeration
export enum RiskLevel {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High'
}

// 4M Category Enumeration
export enum Category {
  MAN = 'Man (人)',
  MACHINE = 'Machine (機械)',
  MATERIAL = 'Material (材料)',
  METHOD = 'Method (方法)'
}

// Case (Safety Report) Entity
export interface Case {
  id: string;
  organizationId: string; // 改善実施記録 (case_resolutions) の RLS で必須
  title: string;
  body: string;
  originalText: string;
  riskLevel: RiskLevel;
  category1: Category;
  category2: string;
  lesson?: string;
  createdAt: string;
  /** 工事現場。現場を指定しない QR からの報告は null。migration 013 で NULL 許容。 */
  teamId: string | null;
  qrPointId?: string;
  status: 'open' | 'in_progress' | 'resolved';
  hasAudio: boolean;
  imageUrl?: string;
  viewerToken?: string; // 匿名投稿者本人が状況確認するための UUID トークン
}

// 改善実施記録のチェックリストキー（migration 018 case_resolutions.actions_checklist）
// 表示は UI 側でラベル変換。値の追加時はマイグレーション不要（jsonb のため）。
export type ResolutionActionTag =
  | 'procedure_review'   // 作業手順の見直し
  | 'training'           // 安全教育の実施
  | 'equipment_fix'      // 設備に対策を施した
  | 'documentation'      // ルールの文書化
  | 'communication';     // 朝礼・周知

// case_resolutions の TS 表現（read/write 共通形）
export interface CaseResolution {
  actionTaken: string;
  actionsChecklist: ResolutionActionTag[];
  effectiveness: 'Low' | 'Medium' | 'High';
  preventionNote: string | null;
  resolvedAt: string;
}

// 投稿者本人が status 確認するときに anon でも取れる subset
// public.get_case_by_viewer_token RPC の戻り値に対応。
// migration 018 以降は resolution（行があれば）も同梱して返す。
export interface CaseStatusView {
  id: string;
  title: string;
  body: string;
  riskLevel: RiskLevel;
  category1: Category;
  category2: string;
  lesson: string | null;
  imageUrl: string | null;
  status: 'open' | 'in_progress' | 'resolved';
  createdAt: string;
  updatedAt: string;
  resolution: CaseResolution | null;
}

/* AiUsageStatus / PlanTier は削除した。
 * 匿名の作業員に「今月のAI分析 8/10回」を見せる機能で使っていたが、
 * その表示自体をやめたため参照ゼロになった（他に使い道が生まれたら復活させる）。
 * 経緯: 表示には anon から organizations.plan を読む必要があり、migration 019 で
 * 権限を剥奪した結果 42501 で静かに失敗していた。 */

// localStorage に保存する「自分の過去の報告」エントリ
export interface MyReportRecord {
  token: string;     // viewer_token
  caseId: string;    // case.id（参考。表示用）
  title: string;     // 投稿時タイトル
  createdAt: string; // ISO 8601
}

// Post Input (Raw submission before AI processing)
export interface PostInput {
  text: string;
  audioBlob?: Blob;
  transcribedText?: string;
  imageBlob?: Blob;
  imagePreview?: string;
}

// AI Analysis Result
export interface AIAnalysisResult {
  title: string;
  formattedBody: string;
  riskLevel: RiskLevel;
  category1: Category;
  category2: string;
  lesson: string;
}

// Submission Data (Combined input + analysis for confirmation screen)
export interface SubmissionData {
  input: PostInput;
  analysis: AIAnalysisResult;
  requiresConfirmation: boolean;
}

// Team Statistics
export interface TeamStats {
  monthPosts: number;
  lastPostDate: string;
  highRiskCount: number;
  improvementCount: number;
  resolvedCount: number;
  ranking?: number;
  previousRanking?: number;
  streak?: number;
  history: number[];
}

// Organization Entity
export interface Organization {
  id: string;
  name: string;
  plan: Plan;
  qrLimit: number;
  qrUsed: number;
  /** 管理画面を使える人数。1席目 ¥2,980・2席目以降 ¥2,480。Stripe の明細が正で webhook が書く。 */
  seats: number;
  createdAt: string;
}

// Plan Types (Legacy - use SubscriptionTier for new code)
export type Plan = 'free' | 'light' | 'standard' | 'enterprise';

// Subscription Tier (matches Stripe tiers)
export type SubscriptionTier = 'free' | 'light' | 'standard' | 'enterprise';

// Subscription Status
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'paused';

// Subscription Entity (Stripe integration)
export interface Subscription {
  id: string;
  userId: string;
  appId: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/**
 * プランの静的な定義。
 *
 * **features / qrLimit などは1席あたりの値で、席数を掛けていない。**
 * 実際の上限は organizations.qr_limit（席数を反映済み）を見ること。
 * features は現時点でどの画面からも参照していない（料金ページは
 * views/PublicViews.tsx に独自に持っている）。表示に使うなら
 * 先に席数連動にすること——さもないと多席の組織に嘘を見せる。
 */
export interface PlanConfig {
  name: string;
  displayName: string;
  price: number;
  qrLimit: number;
  aiAnalysisLimit: number;
  audioTranscriptionLimit: number;
  features: string[];
  stripePriceId?: string;
}

/**
 * 画面から自分で選べる席数の上限。
 *
 * サーバー側（stripe-checkout / update-seats）は 50 まで受け付ける。
 * ここを 20 にしているのは商売上の判断で、これを超える規模は
 * 個別の相談（請求書払い・値引き）になることが多いため。
 * **公開の料金ページに人数の線を書かない**こと——書くとこの値と
 * 食い違い、どちらを信じるかで体験が割れる。
 */
/**
 * 別の組織から招待されているときに、本人へ見せる情報。
 *
 * 合流するとこの人の過去の報告が招待元の管理者から見えるようになる。
 * そのため moves に何かあるときは、実行前に本人の同意を取る。
 */
export interface PendingInvite {
  inviteId: string;
  organizationId: string;
  organizationName: string;
  role: 'admin' | 'leader';
  moves: { cases: number; qrPoints: number; teams: number };
  needsConfirmation: boolean;
}

export const MAX_SELF_SERVE_SEATS = 20;

/** 2席目以降の月額。1席目は PLAN_CONFIGS.light.price（¥2,980）。 */
export const SEAT_PRICE = 2480;

/** 席数から月額を出す。 */
export function priceForSeats(seats: number): number {
  const n = Math.max(seats, 1);
  return PLAN_CONFIGS.light.price + SEAT_PRICE * (n - 1);
}

export const PLAN_CONFIGS: Record<SubscriptionTier, PlanConfig> = {
  free: {
    name: 'free',
    displayName: 'フリー',
    price: 0,
    qrLimit: 3,
    aiAnalysisLimit: 10,
    audioTranscriptionLimit: 5,
    features: ['QRコード3個まで', 'AI分析10回/月', '音声入力5回/月']
  },
  light: {
    name: 'light',
    displayName: 'チーム',
    price: 2980,
    /* ↓ 1席あたりの値。実際の上限は席数を掛ける。
       migration 027 の check_and_increment_usage、および
       analyze-report / why-analysis / transcribe-audio / stripe-webhook の
       各テーブルと必ず一致させる（scripts/check-plan-limits.mjs が検査する）。 */
    qrLimit: 10,
    aiAnalysisLimit: 100,
    audioTranscriptionLimit: 50,
    features: ['QRコード10個まで', 'AI分析100回/月', '音声入力50回/月', 'ダッシュボード機能'],
    stripePriceId: 'price_1SZ9foB6wWR7gVfQOUbLfsm4'
  },
  standard: {
    name: 'standard',
    displayName: 'Standard',
    price: 9800,
    qrLimit: 50,
    aiAnalysisLimit: 1000,
    audioTranscriptionLimit: 500,
    features: ['QRコード50個まで', 'AI分析1,000回/月', '音声入力500回/月', 'ダッシュボード機能', '優先サポート'],
    stripePriceId: 'price_1SZ9hQB6wWR7gVfQiAFNs3Ch'
  },
  enterprise: {
    name: 'enterprise',
    displayName: 'Enterprise',
    // 価格は個別見積り。料金ページには enterprise カードを出していないため 0 のまま。
    price: 0,
    /* 上限は **実際に効いている値** を書く。
     * 以前はすべて 0 で、「お問い合わせください」の意味で置かれていた。
     * だが 0 は「使えない」と読め、実際の判定
     * （genbasafeai_stripe-webhook の TIER_LIMITS / analyze-report /
     *   transcribe-audio のテーブル）とは食い違っていた。
     * 表示都合で 0 を入れると、契約が入った瞬間に「0回と表示されているのに使える」
     * という説明のつかない状態になる。値は実装に合わせ、見せ方は画面側で決める。
     * scripts/check-plan-limits.mjs がこの値と Edge Function 側の一致を検査する。 */
    qrLimit: 500,
    aiAnalysisLimit: 10000,
    audioTranscriptionLimit: 5000,
    features: ['お問い合わせください']
  }
};

// QR Point Entity
export interface QRPoint {
  id: string;
  organizationId: string;
  teamId?: string;
  name: string;
  token: string;
  scans: number;
  posts: number;
  lastScan?: string;
  createdAt: string;
  isActive: boolean;
  equipmentInfo?: string; // 設備情報（AIコンテキスト用）
}

// Team Entity
/**
 * 工事現場。
 *
 * **コードでは team、画面では「工事現場」と呼ぶ。** テーブル名を変えると
 * cases.team_id / qr_points.team_id の参照全域に及ぶため、名前は据え置いた。
 * docs/decisions/ に記録あり。
 */
export interface Team {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  /** 工事の終了日時。終了した現場は新規QRの割当先と現場選択から外れる。 */
  archivedAt?: string | null;
  createdAt: string;
}

/** 組織への招待。 */
export interface OrgInvite {
  id: string;
  organizationId: string;
  email: string;
  role: 'admin' | 'leader';
  status: 'pending' | 'accepted' | 'revoked' | 'seat_blocked';
  expiresAt: string;
  createdAt: string;
}

/** メンバー一覧に出す1行。 */
export interface OrgMember {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'leader';
  createdAt: string;
}

// User Entity (Admin/Leader)
export interface User {
  id: string;
  email: string;
  name: string;
  organizationId: string;
  role: 'admin' | 'leader';
  /** 既定で表示する工事現場。null は「すべての現場」。端末を跨ぐため DB に持つ。 */
  defaultTeamId?: string | null;
  createdAt: string;
}

// QR Token Context (Data embedded in QR code URL)
export interface QRTokenContext {
  token: string;
  teamName: string;
  teamId: string;
  organizationId: string;
  qrPointId?: string;
  qrPointName?: string;
  equipmentInfo?: string; // 設備情報（AIコンテキスト用）
}

// Dashboard Statistics
export interface DashboardStats {
  totalPosts: number;
  highRiskCount: number;
  resolvedCount: number;
  safetyScore: number;
  monthlyTrend: number[];
  categoryDistribution: {
    category: Category;
    count: number;
  }[];
  riskDistribution: {
    level: RiskLevel;
    count: number;
  }[];
}

// Analytics Data
export interface AnalyticsData {
  summary: {
    organizationCount: number;
    userCount: number;
    teamCount: number;
    qrPointCount: number;
    caseCount: number;
    paidSubscriptionCount: number;
  };
  usageByType: {
    counterType: string;
    totalCount: number;
  }[];
  qrPointStats: {
    name: string;
    scans: number;
    posts: number;
    lastScan: string | null;
  }[];
  categoryDistribution: {
    category: string;
    count: number;
  }[];
  riskDistribution: {
    riskLevel: string;
    count: number;
    percentage: number;
  }[];
  dailyPosts: {
    date: string;
    count: number;
  }[];
  planDistribution: {
    plan: string;
    count: number;
  }[];
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Form Validation
export interface ValidationError {
  field: string;
  message: string;
}

// Toast Notification
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

// Audio Recording State
export interface AudioRecordingState {
  isRecording: boolean;
  recordingTime: number;
  audioBlob?: Blob;
  hasRecording: boolean;
  error?: string;
}

// Application State (Zustand Store)
export interface AppState {
  // Current User
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;

  // QR Context (for worker flow)
  qrContext: QRTokenContext | null;

  // Cases
  cases: Case[];

  // Teams
  teams: Team[];

  // QR Points
  qrPoints: QRPoint[];

  // UI State
  isLoading: boolean;
  toasts: Toast[];

  // Actions
  setUser: (user: User | null) => void;
  setOrganization: (org: Organization | null) => void;
  setQRContext: (ctx: QRTokenContext | null) => void;
  addCase: (caseData: Case) => void;
  updateCase: (id: string, updates: Partial<Case>) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  setLoading: (loading: boolean) => void;
}

// Legacy compatibility exports
export type { QRPoint as QREntry };

// ============================================================================
// 運営者向け 機能利用状況（migration 020 の RPC の戻り値）
// ============================================================================
// 注意: ai_usage の fn は ai_usage_log.function の生値。**機能が増えても型を
// 変えずに済むよう文字列のまま扱う**（ocr_import が後から増えた実例がある）。
// 表示名だけラベル表で解決し、無ければ生値をそのまま出す。

export interface OperatorAiUsageRow {
  fn: string;
  mode: 'demo' | 'org' | string;
  d1: number;
  d7: number;
  d30: number;
  total: number;
  tokens: number;
  cached: number;
  orgs: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface OperatorUsageStats {
  generated_at: string;
  totals: {
    organizations: number;
    users: number;
    qr_points: number;
    qr_scans: number;
    qr_posts: number;
    cases: number;
    cases_open: number;
    cases_resolved: number;
    cases_30d: number;
  };
  ai_usage: OperatorAiUsageRow[];
  demo_guard: {
    today: number;
    daily_limit: number;
    d30: number;
    unique_ips_30d: number;
  };
  funnel: {
    registered: number;
    created_qr: number;
    /** migration 024 適用後に現れる。未適用のうちは undefined。 */
    printed?: number;
    scanned: number;
    reported: number;
    resolved: number;
  };
  /** Phase 2 でポスター計測を足したときに現れる。未導入のうちは undefined。 */
  poster_usage?: OperatorAiUsageRow[];
}

/** 運営者向け「最近ログインしたユーザー」1行（migration 022 の RPC の戻り値）。
 *  匿名で報告した作業員は含まれない（管理者側のアカウントのみ）。 */
export interface OperatorRecentLogin {
  auth_id: string;
  email: string | null;
  last_sign_in_at: string | null;
  signed_up_at: string | null;
  user_name: string | null;
  user_role: string | null;
  organization_name: string | null;
  organization_plan: string | null;
  organization_cases: number | null;
  organization_qr_points: number | null;
  /** 登録時のアプリ名。過去の登録には記録が無いので null になる（推測で埋めない）。 */
  signup_app: string | null;
}
